import { Anchor, Box, Divider, Group, Select } from '@byecode/ui'
import type { IndependentAction, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import type { FlowNode, NodeTypes, VariableTree2, WechatSubscriptionMessageNotificationsConfig } from '@lighthouse/shared'
import {
    commonPageList,
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    SelectPage,
    useAtomData,
    workflowGuidanceDocumentMap
} from '@lighthouse/shared'
import { equals, find, uniq } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { lastPageOfStackAtom, pageTreeListAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { ArgInput } from '@/components/ArgInput'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useAppLanguageList, useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useCurrentLanguagePageList, useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { useGetWxMiniProgramMessageInfo } from '@/shared/reusable'

import { GroupRequired } from '../../Common/GroupRequired'
import * as CM from '../../styles'
import { EmptyMiniProgramTemplate } from '../component/EmptyMiniProgramTemplate'
import { MessageTargetPerson } from '../component/MessageTargetPerson'
import { ContentSetting } from './ContentSetting'
import * as SC from './styles'

interface WxSubscriptionNotificationActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatSubscriptionMessageNotificationsConfig
    onChange: (value: WechatSubscriptionMessageNotificationsConfig) => void
}

export const WxSubscriptionNotificationActionConfigure: React.FC<WxSubscriptionNotificationActionConfigureProps> = ({
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const allPages = useCurrentLanguagePageList()
    const openPageId = value.page?.openPageId
    const { viewDsId = '', formDsId = '', viewType } = useActionAdderDepParams()
    const pageTree = useAtomData(pageTreeListAtom)
    const isSystemPage = commonPageList.some(v => v.value === openPageId)

    const languageList = useAppLanguageList()

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { type } = useFlowContainerContext()

    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const { data } = useGetWxMiniProgramMessageInfo(appId)

    const formDataSource = useMemo(() => dataSourceList.find(ds => ds.id === formDsId), [dataSourceList, formDsId])
    const viewDataSource = useMemo(() => dataSourceList.find(ds => ds.id === viewDsId), [dataSourceList, viewDsId])

    const [_, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const currentPageArgs = useMemo(() => {
        if (!openPageId || isSystemPage) {
            return
        }

        const page = allPages.find(item => item.id === openPageId)
        if (!page) {
            return
        }

        return {
            pathArgs: page.parameterAndVariable?.pathParameter,
            queryArgs: page.parameterAndVariable?.queryParameter
        }
    }, [allPages, isSystemPage, openPageId])

    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [customViewOption, viewDataSource, viewType, isInFlow])

    const formOption = useMemo(
        () =>
            formDataSource && !isInFlow
                ? getFormDatasourceOptions({
                      dataSource: formDataSource
                  })
                : undefined,
        [formDataSource, isInFlow]
    )

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: (allParentNodes && getAllCanBeUpstreamNodes(allParentNodes)) || [],
                allUpstreamNodes: allParentNodes || [],
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const templateUserOption = useGetUserRecordOptions()

    const wechatMessageTemplatesOptions = useMemo(() => {
        return (
            data?.map(item => ({
                value: item.templateId ?? '',
                label: item.title ?? '',
                content: item.content ?? ''
            })) ?? []
        )
    }, [data])

    const handleTemplateChange = useCallback(
        (templateId: string) => {
            const currentOption = find(option => option.value === templateId, wechatMessageTemplatesOptions)?.content ?? ''
            const variablesMatches = currentOption.match(/{{([\w.]+)}}/g) ?? []
            const variables = variablesMatches.map(item => {
                return item.replace(/{{(\w+)\.\w+}}/g, '$1')
            })
            const uniqVariables = uniq(variables)

            const currentValues = value.values ?? []
            const valuesVariableNames = currentValues.map(v => v.variableName)

            const next = { ...value, templateId }

            if (equals(valuesVariableNames, uniqVariables)) {
                onChange(next)
                return
            }
            const newValues = uniqVariables.map(field => {
                const v = find(item => item.variableName === field, currentValues)?.value ?? (null as unknown as VariableADTvalue)
                return { variableName: field, value: v }
            })
            onChange({ ...next, values: newValues })
        },
        [onChange, value, wechatMessageTemplatesOptions]
    )

    return (
        <CM.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <CM.FormItemLabelWrapper>
                        <CM.FormItemLabel>类型</CM.FormItemLabel>
                    </CM.FormItemLabelWrapper>
                    <CM.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </CM.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="发送给" required>
                <Box px={8}>
                    <SC.DescriptionWrapper>
                        <SC.Description>
                            请选择{' '}
                            <Link to={`/${appId}/dataSource/USER_DATASOURCE`} target="_blank">
                                用户表
                            </Link>{' '}
                            中包含“微信网页授权_OpenID”的人员或人员变量，除此之外人员要在微信关注公众号才能接收到消息。
                        </SC.Description>
                    </SC.DescriptionWrapper>
                    <MessageTargetPerson
                        workflowList={workflowList}
                        dataSourceList={dataSourceList}
                        parentNodes={allParentNodes}
                        value={value.personList}
                        onChange={personList => onChange({ ...value, personList })}
                    />
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="消息模版" required>
                <Box px={8}>
                    <SC.SelectContainer>
                        <Select
                            placeholder="请选择"
                            options={wechatMessageTemplatesOptions}
                            value={value.templateId}
                            emptyComponent={<EmptyMiniProgramTemplate />}
                            onChange={templateId => handleTemplateChange(templateId)}
                        />
                    </SC.SelectContainer>
                    <SC.Description marginTop>
                        模板消息需在小程序后台中配置{' '}
                        <Anchor to={workflowGuidanceDocumentMap.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION.url} target="_blank">
                            详见
                        </Anchor>
                    </SC.Description>
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired label="模板参数" required>
                <Box px={8}>
                    <SC.Description>
                        微信针对不同类型的模板参数有相应要求，如果不符合会造成消息发送失败，具体要求{' '}
                        <Anchor to={workflowGuidanceDocumentMap.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION.url} target="_blank">
                            详见
                        </Anchor>
                    </SC.Description>
                    <ContentSetting
                        value={value.values}
                        onChange={values => onChange({ ...value, values })}
                        viewOptions={viewOptions}
                        formOption={formOption}
                        pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined}
                        upstreamOptions={upstreamOptions}
                        userOption={templateUserOption}
                        dataSourceList={dataSourceList}
                    />
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <Group label="页面详情页">
                <ListItem4ByecodeUi>
                    <CM.FormItemLabelWrapper>
                        <CM.FormItemLabel>页面</CM.FormItemLabel>
                    </CM.FormItemLabelWrapper>
                    <CM.FormItemContent>
                        <SelectPage
                            dropdownProps={{
                                style: {
                                    maxHeight: 300
                                }
                            }}
                            withinPortal
                            languageList={languageList}
                            value={value.page?.openPageId}
                            options={pageTree}
                            onChange={val => onChange({ ...value, page: { ...value.page, openPageId: val } })}
                        />
                    </CM.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            {currentPageArgs?.pathArgs && currentPageArgs.pathArgs.length > 0 && (
                <>
                    <Divider />
                    <Group label="传入路径参数">
                        {(() => {
                            const parametersValue = value.page?.pathParameters || []
                            const pathSchemas = currentPageArgs.pathArgs
                            return (
                                <>
                                    {pathSchemas?.map(item => {
                                        const v = parametersValue?.find(p => p.id === item.id)?.value
                                        return (
                                            <ListItem4ByecodeUi key={item.id}>
                                                <CM.FormItemContent style={{ flex: 1 }}>
                                                    <ArgInput
                                                        autoHeight
                                                        enableSelectDataSource
                                                        data={item}
                                                        allParentNodes={allParentNodes}
                                                        value={v}
                                                        onChange={nextVal => {
                                                            const newParameters = pathSchemas.map(schema => {
                                                                const isCurrent = schema.id === item.id
                                                                const cur = isCurrent
                                                                    ? nextVal
                                                                    : parametersValue?.find(f => f.id === schema.id)?.value
                                                                return {
                                                                    id: schema.id,
                                                                    value: cur
                                                                }
                                                            })
                                                            onChange({ ...value, page: { ...value.page, pathParameters: newParameters } })
                                                        }}
                                                    />
                                                </CM.FormItemContent>
                                            </ListItem4ByecodeUi>
                                        )
                                    })}
                                </>
                            )
                        })()}
                    </Group>
                </>
            )}

            {currentPageArgs?.queryArgs && currentPageArgs.queryArgs.length > 0 && (
                <>
                    <Divider />
                    <Group label="传入查询参数">
                        {(() => {
                            const parametersValue = value.page?.queryParameters || []
                            const querySchemas = currentPageArgs.queryArgs
                            return (
                                <>
                                    {querySchemas?.map(item => {
                                        const v = parametersValue?.find(p => p.id === item.id)?.value
                                        return (
                                            <ListItem4ByecodeUi key={item.id}>
                                                <CM.FormItemContent style={{ flex: 1 }}>
                                                    <ArgInput
                                                        autoHeight
                                                        data={item}
                                                        enableSelectDataSource
                                                        allParentNodes={allParentNodes}
                                                        value={v}
                                                        onChange={nextVal => {
                                                            const newParameters = querySchemas.map(schema => {
                                                                const isCurrent = schema.id === item.id
                                                                const cur = isCurrent
                                                                    ? nextVal
                                                                    : parametersValue?.find(f => f.id === schema.id)?.value
                                                                return {
                                                                    id: schema.id,
                                                                    value: cur
                                                                }
                                                            })
                                                            onChange({ ...value, page: { ...value.page, queryParameters: newParameters } })
                                                        }}
                                                    />
                                                </CM.FormItemContent>
                                            </ListItem4ByecodeUi>
                                        )
                                    })}
                                </>
                            )
                        })()}
                    </Group>
                </>
            )}
        </CM.Container>
    )
}
